import React, { useMemo, useState } from 'react';
import { Box, Flex, Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { MagnifyingGlass } from 'phosphor-react';

import { IFolhaColaborador } from '../../../../hooks/useFinanceiro';
import { Segmented } from '../components/Segmented';
import { FolhaRoster, PessoasAnalytics } from './PessoasAnalytics';

interface PessoasTabProps {
  /** Já vem filtrado pelo status global do módulo (Ativos/Demitidos/Todos). */
  colaboradores: IFolhaColaborador[];
  /** Muda quando o status global muda — força o roster/gráficos a remontar
   * (evita linhas stale ao alternar Ativos/Demitidos), preservando view/busca. */
  statusKey?: string;
}

/**
 * Aba "Pessoas": alterna entre Distribuições (gráficos) e Lista (roster). A busca
 * por nome age na lista. O filtro de status é global do módulo (fica no header).
 */
export const PessoasTab = ({ colaboradores, statusKey }: PessoasTabProps) => {
  const [view, setView] = useState('dist');
  const [busca, setBusca] = useState('');

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return colaboradores;
    return colaboradores.filter(c => (c.nome || '').toLowerCase().includes(q));
  }, [colaboradores, busca]);

  const qtd = (view === 'lista' ? lista : colaboradores).length;

  return (
    <Box>
      <Flex gap={3} wrap="wrap" align="center" mb={5}>
        <Segmented
          value={view}
          onChange={setView}
          accent="#DB2777"
          options={[
            { value: 'dist', label: 'Distribuições' },
            { value: 'lista', label: 'Lista' },
          ]}
        />
        {view === 'lista' && (
          <InputGroup size="sm" maxW={{ base: 'full', md: '280px' }}>
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass size={15} color="#94A3B8" />
            </InputLeftElement>
            <Input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar colaborador..."
              borderRadius="lg"
              bg="white"
              borderColor="rgba(15,23,42,0.1)"
              _focusVisible={{ borderColor: '#DB2777', boxShadow: '0 0 0 1px #DB2777' }}
            />
          </InputGroup>
        )}
        <Text ml="auto" fontSize="sm" color="#64748B" whiteSpace="nowrap">
          {qtd} colaborador{qtd === 1 ? '' : 'es'}
        </Text>
      </Flex>

      {view === 'dist' ? (
        <PessoasAnalytics key={statusKey} colaboradores={colaboradores} />
      ) : (
        <FolhaRoster key={statusKey} colaboradores={lista} />
      )}
    </Box>
  );
};
